import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SERVER } from '../config/config';
import ServerImage from './ServerImage';

const Hero = () => {
  const navigate = useNavigate();
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${SERVER}/hero-gallery`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching hero gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCTAClick = () => {
    navigate('/book-consultation');
  };

  // Vertical Auto-scrolling Gallery Component
  const AutoScrollGallery = ({ images }) => {
    if (!images || images.length === 0) return null;

    // Split images into 3 columns for the grid layout
    const createColumns = (images, numColumns = 3) => {
      const columns = Array.from({ length: numColumns }, () => []);
      // Triple the images for smoother seamless loop
      const tripleImages = [...images, ...images, ...images];
      tripleImages.forEach((image, index) => {
        columns[index % numColumns].push(image);
      });
      return columns;
    };

    const columns = createColumns(images);

    // Function to get rotation angle based on column position
    const getColumnRotation = (columnIndex) => {
      switch (columnIndex) {
        case 0: // Left column - rotate 35deg to the right
          return 'rotateY(35deg)';
        case 1: // Center column - tilt inward to form trapezoid
          return 'rotateY(0deg) perspective(800px) rotateX(10deg)';
        case 2: // Right column - rotate 35deg to the left
          return 'rotateY(-35deg)';
        default:
          return 'rotateY(0deg)';
      }
    };

    return (
      <div className="w-full overflow-hidden max-md:pb-16 relative">
        {/* 3D Perspective Container */}
        <div 
          className="w-full md:max-w-4xl mx-auto px-2 md:px-4 relative"
          style={{
            perspective: '1000px',
            perspectiveOrigin: 'center top'
          }}
        >
          <div 
            className="grid grid-cols-3 gap-2 md:gap-4 h-60 md:h-80 overflow-hidden relative mx-auto"
            style={{
              transform: 'rotateX(50deg)',
              transformStyle: 'preserve-3d',
              maxWidth: '100%'
            }}
          >
            {columns.map((column, columnIndex) => (
              <div
                key={columnIndex}
                className="flex flex-col gap-2 animate-scroll-vertical relative"
                style={{
                  animation: `scrollVertical${columnIndex + 1} ${20 + columnIndex * 5}s linear infinite`,
                  transform: `translateY(${columnIndex % 2 === 0 ? '0' : '-50%'}) ${getColumnRotation(columnIndex)}`,
                  transformStyle: 'preserve-3d',
                  transformOrigin: 'center center'
                }}
              >
                {column.map((image, index) => (
                  <div
                    key={`${image.id}-${columnIndex}-${index}`}
                    className="flex-shrink-0 rounded-lg md:rounded-2xl overflow-hidden transition-all duration-300 group relative h-[60px] md:h-[140px]"
                    style={{
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <ServerImage
                      src={image.image}
                      alt={image.alt_text || image.title}
                      fallback="/hero.png"
                      className="w-full h-full object-cover group-hover:scale-105 rounded-md md:rounded-2xl transition-transform duration-300"
                      style={{ 
                       
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden'
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Cloud-like Blur Effect */}
        <div className="absolute -bottom-1 left-0 right-0 h-32 pointer-events-none overflow-hidden">
          {/* Cloud shape using SVG */}
          <svg 
            className="absolute bottom-0 w-full h-full" 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none"
            style={{ transform: 'translateY(10%)' }}
          >
            <defs>
              <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: 'rgba(248, 249, 250, 0)', stopOpacity: 0.2 }} />
                <stop offset="30%" style={{ stopColor: 'rgba(248, 249, 250, 0.3)', stopOpacity: 0.3 }} />
                <stop offset="60%" style={{ stopColor: 'rgba(248, 249, 250, 0.7)', stopOpacity: 0.7 }} />
                <stop offset="100%" style={{ stopColor: 'rgba(248, 249, 250, 1)', stopOpacity: 1 }} />
              </linearGradient>
              <filter id="blur">
                <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
              </filter>
            </defs>
            
            {/* Multiple cloud layers for depth */}
            <path 
              d="M0,60 Q150,20 300,50 T600,60 T900,50 T1200,60 L1200,120 L0,120 Z" 
              fill="url(#cloudGradient)"
              filter="url(#blur)"
              opacity="0.8"
            />
            <path 
              d="M0,70 Q200,30 400,60 T800,70 T1200,60 L1200,120 L0,120 Z" 
              fill="url(#cloudGradient)"
              filter="url(#blur)"
              opacity="0.9"
            />
            <path 
              d="M0,80 Q100,50 200,70 T400,80 T600,75 T800,80 T1000,75 T1200,80 L1200,120 L0,120 Z" 
              fill="rgba(248, 249, 250, 1)"
              filter="url(#blur)"
            />
          </svg>
          
          {/* Additional soft gradient overlay */}
          <div 
            className="absolute bottom-0 w-full h-full"
            style={{
              background: 'linear-gradient(to top, rgba(248, 249, 250, 1) 0%, rgba(248, 249, 250, 0.95) 20%, rgba(248, 249, 250, 0.6) 50%, rgba(248, 249, 250, 0) 100%)',
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <section className="h-[600px] md:min-h-screen  relative flex flex-col w-full">
      {/* Main Content - Center */}
      <div className="flex-grow flex items-center justify-center pt-0 pb-0 md:pb-0">
        <div className="relative z-20 text-center px-4 md:px-6 w-full max-w-5xl mx-auto">
          <h1 className="text-2xl md:text-4xl lg:text-[40px] font-bold leading-tight md:leading-[50px] text-[#111111] mb-6 tracking-tight">
            Build Your Digital Empire 
            <br  />
            <span className="text-[#1D44E9] relative">
              Websites, Apps, Branding & Design {""}
            </span>
            
            That Dominate
          </h1>
          
          <p className="text-sm md:text-xl text-black font-normal mb-6 md:mb-10 leading-relaxed max-w-2xl mx-auto">
            We don't just build products. We engineer growth engines
            <br className="hidden md:block" />
            that attract, convert, and scale.
          </p>
          
          <div className="flex items-center justify-center gap-2 md:gap-4">
            <button shadow
              className="inline-flex items-center gap:1 md:gap-3 bg-[#1D44E9] text-white border-0 pl-3 md:pl-8 pr-2 py-2 rounded-full text-lg font-semibold cursor-pointer transition-all duration-300 -[0_10px_20px_rgba(29,68,233,0.3)] hover:bg-blue-700 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(29,68,233,0.4)] active:translate-y-0 group"
              onClick={handleCTAClick}
            >
              <span className="py-1 md:py-1 text-xs md:text-lg">Get Your Free Growth Audit</span>
              <div className="flex items-center justify-center h-6 w-6 md:w-8 md:h-8 bg-white rounded-full transition-transform duration-300 group-hover:translate-x-1 ml-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M5 12h14M12 5l7 7-7 7" 
                    stroke="#000000ff" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
            
            <button 
              className="inline-flex text-sm md:text-lg items-center gap-3 bg-[#1D44E9] text-white border-0 px-3 md:px-8 py-2 md:py-3 rounded-full text-lg font-semibold cursor-pointer transition-all duration-300 shadow-[0_10px_20px_rgba(29,68,233,0.3)] hover:bg-blue-700 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(29,68,233,0.4)] active:translate-y-0"
              onClick={() => window.open('https://wa.me/21628655323', '_blank')}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.690"/>
              </svg>
              <span className='max-md:hidden'>Whatsapp</span>
            </button>
          </div>
        </div>
      </div>

      {/* Vertical Auto-scrolling Gallery - Bottom */}
      {!loading && galleryImages.length > 0 && (
        <div className="w-full relative z-10 flex-shrink-0 -mb-2 md:-mb-1">
          <AutoScrollGallery images={galleryImages} />
        </div>
      )}
    </section>
  );
};

export default Hero;